import ctl from '@netlify/classnames-template-literals'
import { AnimatePresence, motion } from 'motion/react'
import { ReactNode } from 'react'
import { AnimateChangeInHeight } from '../../../rapid-ui'

export type SettingsStatusBoxProps = {
	status: 'enabled' | 'disabled' | 'pending'
	children?: ReactNode
	statusText?: ReactNode
	icon?: ReactNode
}

export function SettingsStatusBox({ status, children, statusText, icon }: SettingsStatusBoxProps) {
	const outerClasName = ctl(`
        rounded-lg p-1 ring bg-gradient-to-b
        ${status === 'enabled' ? 'from-brand-50 to-brand-100 ring-brand-200' : ''}
        ${status === 'disabled' ? 'from-red-50 to-red-100 ring-red-200' : ''}
        ${status === 'pending' ? 'from-neutral-50 to-neutral-100 ring-neutral-200' : ''}
		transition-colors
		duration-500
    `)

	const innerClassName = ctl(`
       ring rounded-md p-4 bg-white/90
        ${status === 'enabled' ? 'ring-brand-300' : ''}
        ${status === 'disabled' ? 'ring-red-300' : ''}
        ${status === 'pending' ? 'ring-neutral-300' : ''}
        shadow-sm
        text-sm
		transition-colors
        duration-500
    `)

	const statusTextClassName = ctl(`
        text-base font-medium
        ${status === 'enabled' ? 'text-brand-600' : ''}
        ${status === 'disabled' ? 'text-red-600' : ''}
        ${status === 'pending' ? 'text-neutral-600' : ''}
		transition-colors
		duration-500
    `)

	const iconClassName = ctl(`
        ${status === 'enabled' ? 'text-brand-600' : ''}
        ${status === 'disabled' ? 'text-red-600' : ''}
        ${status === 'pending' ? 'text-neutral-600' : ''}
		transition-colors
		duration-500
    `)

	return (
		<div className={outerClasName}>
			<div className={innerClassName}>
				<div className="grid grid-cols-[minmax(min-content,max-content)_1fr] gap-2">
					<div className={iconClassName}>
						<AnimatePresence mode="wait" initial={false}>
							<motion.div
								initial={{ opacity: 0, scale: 0.5 }}
								animate={{ opacity: 1, scale: 1 }}
								exit={{ opacity: 0, scale: 0.5 }}
								key={status}
							>
								{icon}
							</motion.div>
						</AnimatePresence>
					</div>

					<div className="flex items-center gap-2">
						<AnimatePresence mode="wait" initial={false}>
							<motion.div
								initial={{ opacity: 0, translateY: 7 }}
								animate={{ opacity: 1, translateY: 0 }}
								exit={{ opacity: 0, translateY: -7 }}
								key={status}
							>
								<div className={statusTextClassName}>{statusText}</div>
							</motion.div>
						</AnimatePresence>
					</div>

					<>
						{/* Spacer */}
						<div />
						<AnimateChangeInHeight duration={0.4}>
							<div className="flex flex-col gap-2">
								{children}
							</div>
						</AnimateChangeInHeight>
					</>
				</div>
			</div>
		</div>
	)
}
